"use client"

import type React from "react"
import { createContext, useContext, useState, useEffect,  ReactNode } from "react"
import ApiService, {  LoginCredentials,  RegisterUserData,  UserProfile, ChangePassword , ConnectorsConfig , getAuthTokenRequest} from "../services/api"

interface AuthContextType {
  user: UserProfile | null
  login: (credentials: LoginCredentials) => Promise<any>
  register: (userData: RegisterUserData) => Promise<any>
  updateUserDetails : (userData : any) => Promise<any>
  updateConnectorConfig : (userData : any) => Promise<any>
  deleteConnectorConfig : (userData : any) => Promise<any>
  getConnectorDetails : (userData : any) => Promise<any>
  getAuthStatus : (getAuthTokenRequest : getAuthTokenRequest) => Promise<any>
  changePassword : (userData : ChangePassword) => Promise<any>
  authoriseHubspot: () => Promise<any>
  getUserDetails: () => Promise<any>
  getSavedConnectors: () => Promise<any>
  getAllFields :(tanentId: Number, connectorId: Number) => Promise<any>
  saveConnectorConfig : (userData: ConnectorsConfig) => Promise<any>
  authoriseSalesforce: (tenant_id: Number) => Promise<any>
  getConnectors: () => Promise<any>
  logout: () => void
  loading: boolean
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  // Check for existing token and validate it on app load
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem("token")

      if (token) {
        try {
          // Validate token by fetching user profile
          // const userData = await ApiService.getUserProfile()
          let getUserData = localStorage.getItem('userData')
          if(getUserData){
            const userData = JSON.parse(getUserData)
            setUser(userData)
            setIsAuthenticated(true)
          }else{
            throw new Error("No refresh token available")
          }

        } catch (error) {
          // Token is invalid or expired
          console.error("Token validation failed:", error)
          localStorage.removeItem("token")
          localStorage.removeItem("userData")
          setUser(null)
          setIsAuthenticated(false)
        }
      } else {
        setUser(null)
        setIsAuthenticated(false)
      }

      setLoading(false)
    }

    initializeAuth()
  }, [])

  // Listen for storage changes (useful for multi-tab scenarios)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "token") {
        if (e.newValue === null) {
          // Token was removed
          setUser(null)
          setIsAuthenticated(false)
        } else if (e.newValue && !user) {
          // Token was added, reload user data
          loadUser()
        }
      }
    }

    window.addEventListener("storage", handleStorageChange)
    return () => window.removeEventListener("storage", handleStorageChange)
  }, [user])

  const loadUser = async () => {
    try {
      const userData = await ApiService.getUserProfile()
      setUser(userData)
      setIsAuthenticated(true)
    } catch (error) {
      console.error("Failed to load user:", error)
      localStorage.removeItem("token")
      setUser(null)
      setIsAuthenticated(false)
    }
  }

  const login = async (credentials: LoginCredentials) => {
    try {
      const response = await ApiService.login(credentials)
      if(response.status !== 200){
        throw response
      }
      // Store token
      localStorage.setItem("token", response.data.access_token)
      localStorage.setItem("refresh_token", response.data.refresh_token)
      localStorage.setItem("token_type", response.data.token_type)

      // Set user data
      setUser(response?.data?.user as UserProfile)
      setIsAuthenticated(true)

      return response
    } catch (error) {
      setUser(null)
      setIsAuthenticated(false)
      throw error
    }
  }

  const register = async (userData: RegisterUserData) => {
    try {
      const response = await ApiService.register(userData)
      return response
    } catch (error) {
      throw error
    }
  }

  const authoriseSalesforce = async (tenant_id: any) => {
    try {
      const response = await ApiService.authoriseSalesforce(tenant_id)
      return response
    } catch (error) {
      throw error
    }
  }

  const authoriseHubspot = async () => {
    try {
      const response = await ApiService.authoriseHubspot()
      return response
    } catch (error) {
      throw error
    }
  }

  const getConnectors = async () => {
    try {
      const response = await ApiService.getConnectors()
      return response
    } catch (error) {
      throw error
    }

  }

  const getAllFields = async (tanentId: any, connectorId: any) => {
    try {
      const response = await ApiService.getAllFields(tanentId, connectorId)
      return response
    } catch (error) {
      throw error
    }

  }

  const updateUserDetails = async (userData: any) =>{
    try{
      const response = await ApiService.updateUserProfile(userData)
      return response
    }catch(err){
      throw err
    }
 
  }

  const saveConnectorConfig = async (userData: ConnectorsConfig) =>{
    try{
      const response = await ApiService.saveConnectorConfig(userData)
      return response
    }catch(err){
      throw err
    }
 
  }

  const getUserDetails = async() => {
    try {
      const response = await ApiService.getUserProfile()
      return response
    } catch (error) {
      throw error
    }
  }

  const changePassword = async (data: ChangePassword) =>{
    try{
        const response = await ApiService.changePassword(data)
        return response
    }catch(err){
      throw err
    }

  }

  const getSavedConnectors = async () => {
    try {
      const response = await ApiService.getSavedConnectors()
      return response
    } catch (error) {
      throw error
    }
  }

  const updateConnectorConfig = async (data :any)=>{
    try{
        const response = await ApiService.updateConnectorConfig(data)
        return response
    }catch(err){
      throw err
    }
  }

  const deleteConnectorConfig = async (data :any)=>{
    try{
        const response = await ApiService.deleteConnectorConfig(data)
        return response
    }catch(err){
      throw err
    }
  }

  const getConnectorDetails = async (tanentConnectorId: number) =>{
     try {
      const response = await ApiService.getConnectorDetail(tanentConnectorId)
      return response
    } catch (error) {
      throw error
    }
  }

   const getAuthStatus = async (getAuthTokenRequest: getAuthTokenRequest) =>{
     try {
      const response = await ApiService.getAuthStatus(getAuthTokenRequest.tanentConnectorId, getAuthTokenRequest.tanentId)
      return response
    } catch (error) {
      throw error
    }

  }

  

  const logout = async () => {
    try {
      await ApiService.logout();
      setUser(null)
      setIsAuthenticated(false)
    } catch (error) {
      console.log(error)
    }

  }


  const value: AuthContextType = {
    user,
    login,
    register,
    logout,
    authoriseHubspot,
    authoriseSalesforce,
    saveConnectorConfig,
    getConnectors,
    getAllFields,
    updateUserDetails,
    getUserDetails,
    getSavedConnectors,
    changePassword,
    updateConnectorConfig,
    deleteConnectorConfig,
    getConnectorDetails,
    getAuthStatus,
    loading,
    isAuthenticated,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
